import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { DeliveryNote } from '../../../types';
import { findCodePrefix, nextSequence } from '../../../utils/codePrefixUtils';
import Input from '../Input';
import Select from '../Select';

interface DeliveryNoteHeaderProps {
  register: UseFormRegister<DeliveryNote>;
  errors: FieldErrors<DeliveryNote>;
  codePrefixes: any[];
  selectedCodePrefix: string;
  setSelectedCodePrefix: (value: string) => void;
  dnSeqNumber: string;
  setDnSeqNumber: (value: string) => void;
  setValue: (name: any, value: any, options?: any) => void;
  requisitionOptions: { value: string; label: string }[];
  isEditing: boolean;
  hasRequisitionId: boolean;
}

const DeliveryNoteHeader: React.FC<DeliveryNoteHeaderProps> = ({
  register,
  errors,
  codePrefixes,
  selectedCodePrefix,
  setSelectedCodePrefix,
  dnSeqNumber,
  setDnSeqNumber,
  setValue,
  requisitionOptions,
  isEditing,
  hasRequisitionId,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Petromarine Offshore Projects Support Co.</h2>
        <p className="text-sm text-gray-600">DELIVERY NOTE</p>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delivery Note Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={selectedCodePrefix}
                onChange={(e) => {
                  setSelectedCodePrefix(e.target.value);
                  const prefix = findCodePrefix(codePrefixes, e.target.value);
                  if (prefix) {
                    const nextNum = String(nextSequence(prefix)).padStart(3, '0');
                    setDnSeqNumber(nextNum);
                    setValue('deliveryNoteNumber', `${prefix.prefix}${nextNum}`, { shouldValidate: true });
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isEditing}
              >
                <option value="">Select Code Prefix</option>
                {codePrefixes.map((prefix: any) => (
                  <option key={prefix.id} value={prefix.id}>
                    {prefix.prefix} - {prefix.name || prefix.description || ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedCodePrefix && (
              <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-sm text-gray-600">Generated Code: </span>
                <span className="text-sm font-semibold text-primary-600">
                  {findCodePrefix(codePrefixes, selectedCodePrefix)?.prefix}{dnSeqNumber}
                </span>
              </div>
            )}
            <input type="hidden" {...register('deliveryNoteNumber', { required: 'Delivery note number is required' })} />
            {errors.deliveryNoteNumber?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.deliveryNoteNumber.message}</p>
            )}
          </div>
          <div>
            <Select
              label="Related Requisition"
              {...register('requisitionId', { required: 'Requisition is required' })}
              options={requisitionOptions}
              error={errors.requisitionId?.message}
              disabled={hasRequisitionId || isEditing}
            />
          </div>
          <div>
            <Input
              label="Page Number"
              {...register('pageNumber')}
              placeholder="e.g., Page: 1 of 1"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryNoteHeader;
